import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import BASICS_CURRICULUM from '../../library/curriculum/basics';
import toast from 'react-hot-toast';

export default function CurriculumViewer({ activeClass, syncToLiveBoard, switchMode }) {
    const [expandedClassId, setExpandedClassId] = useState(null);

    // Group curriculum by category
    const categories = BASICS_CURRICULUM.reduce((acc, cls) => {
        if (!acc[cls.category]) {
            acc[cls.category] = [];
        }
        acc[cls.category].push(cls);
        return acc;
    }, {});

    const handleLoadDemo = async (fen) => {
        if (!fen) return;
        const loading = toast.loading("Loading demo position to board...");
        try {
            await syncToLiveBoard(fen, 'fen');
            toast.success("Demo position loaded!", { id: loading });
        } catch (e) {
            toast.error("Failed to load position.", { id: loading });
        }
    };

    const handleAssignPuzzle = async (puzzle, classId) => {
        if (!puzzle || !puzzle.fen || !puzzle.solution) return;
        const loading = toast.loading("Launching live quiz for students...");
        try {
            const answerArray = puzzle.solution.split(',').map(s => s.trim());
            await updateDoc(doc(db, "classes", activeClass.id), {
                classMode: 'QUIZ',
                currentFen: puzzle.fen,
                studentFens: {},
                quizData: {
                    question: puzzle.prompt || "Solve this puzzle!",
                    answers: answerArray,
                    endTime: Date.now() + 120000, // 2 minutes duration
                    points: 10,
                    active: true,
                    solvedBy: []
                }
            });
            toast.success("Live Quiz Started! 🚀", { id: loading });
        } catch (e) {
            toast.error("Failed to launch quiz.", { id: loading });
        }
    };

    const handleStartMinigame = async (pieceName) => {
        if (!pieceName) return;
        const loading = toast.loading(`Starting ${pieceName} Practice...`);
        try {
            await updateDoc(doc(db, "classes", activeClass.id), {
                classMode: 'MINIGAME',
                basicsPiece: pieceName,
                studentBasicsStats: {}
            });
            toast.success(`${pieceName} Practice Mode started! 🍎`, { id: loading });
        } catch (e) {
            toast.error("Failed to start practice.", { id: loading });
        }
    };

    return (
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, gap: '10px', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
            <div style={{ background: 'rgba(139, 92, 246, 0.08)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(139, 92, 246, 0.25)', marginBottom: '5px' }}>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-primary)' }}>🎓 Chess Basics Curriculum</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                    Select a class from the 36-lesson syllabus below to access theory guidelines, load setups, or push puzzles/games.
                </p>
            </div>

            {Object.entries(categories).map(([categoryName, classes]) => (
                <div key={categoryName} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '3px' }}>
                        {categoryName}
                    </div>
                    {classes.map(cls => {
                        const isExpanded = expandedClassId === cls.id;
                        return (
                            <div 
                                key={cls.id} 
                                style={{ 
                                    background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent', 
                                    border: '1px solid rgba(163, 177, 198, 0.2)', 
                                    borderRadius: '8px', 
                                    overflow: 'hidden', 
                                    transition: 'all 0.2s' 
                                }}
                            >
                                <div 
                                    onClick={() => setExpandedClassId(isExpanded ? null : cls.id)}
                                    style={{ 
                                        padding: '10px 12px', 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        cursor: 'pointer',
                                        background: isExpanded ? 'rgba(255,255,255,0.03)' : 'transparent'
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                            Class {cls.id}: {cls.title}
                                        </span>
                                        {!isExpanded && (
                                            <span style={{ fontSize: '9.5px', color: 'var(--text-secondary)' }}>
                                                {cls.description}
                                            </span>
                                        )}
                                    </div>
                                    <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                                        {isExpanded ? '▲' : '▼'}
                                    </span>
                                </div>

                                {isExpanded && (
                                    <div style={{ padding: '12px', borderTop: '1px solid rgba(163, 177, 198, 0.15)', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '11px', background: 'rgba(0,0,0,0.05)' }}>
                                        <div style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                                            {cls.description}
                                        </div>
                                        
                                        <div style={{ background: 'rgba(245, 158, 11, 0.04)', borderLeft: '3px solid #f59e0b', padding: '8px', borderRadius: '0 6px 6px 0', color: 'var(--text-primary)', lineHeight: '1.5' }}>
                                            <strong>💡 Teaching Theory:</strong><br />
                                            {cls.theory}
                                        </div>

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                                            {cls.demoFen && (
                                                <button 
                                                    onClick={() => handleLoadDemo(cls.demoFen)}
                                                    style={{ 
                                                        flex: '1 1 calc(50% - 3px)', 
                                                        padding: '6px 8px', 
                                                        background: '#3b82f6', 
                                                        color: 'white', 
                                                        border: 'none', 
                                                        borderRadius: '6px', 
                                                        fontWeight: 'bold', 
                                                        cursor: 'pointer',
                                                        fontSize: '10px'
                                                    }}
                                                >
                                                    ♟️ Demo Board
                                                </button>
                                            )}
                                            {cls.minigame && (
                                                <button 
                                                    onClick={() => handleStartMinigame(cls.minigame.piece)}
                                                    style={{ 
                                                        flex: '1 1 calc(50% - 3px)', 
                                                        padding: '6px 8px', 
                                                        background: '#10b981', 
                                                        color: 'black', 
                                                        border: 'none', 
                                                        borderRadius: '6px', 
                                                        fontWeight: 'bold', 
                                                        cursor: 'pointer',
                                                        fontSize: '10px'
                                                    }}
                                                >
                                                    🍎 Start {cls.minigame.piece} Game
                                                </button>
                                            )}
                                        </div>

                                        {cls.puzzles && cls.puzzles.length > 0 && (
                                            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '10px', textTransform: 'uppercase' }}>🧩 Live Puzzles:</span>
                                                {cls.puzzles.map((puz, pIdx) => (
                                                    <div 
                                                        key={pIdx} 
                                                        style={{ 
                                                            padding: '6px 8px', 
                                                            background: 'rgba(255,255,255,0.02)', 
                                                            border: '1px dashed rgba(163, 177, 198, 0.3)', 
                                                            borderRadius: '6px', 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '9.5px', color: 'var(--text-primary)', flex: 1 }}>
                                                            {puz.prompt}
                                                        </span>
                                                        <button 
                                                            onClick={() => handleAssignPuzzle(puz, cls.id)}
                                                            style={{ 
                                                                padding: '4px 8px', 
                                                                background: '#8b5cf6', 
                                                                color: 'white', 
                                                                border: 'none', 
                                                                borderRadius: '4px', 
                                                                fontWeight: 'bold', 
                                                                cursor: 'pointer',
                                                                fontSize: '9px',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            🚀 Assign
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}
